// TourProvider.js - Enhanced with absolute force show capability
import React, { createContext, useContext, useState, useCallback } from 'react';

const TourContext = createContext();

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

export const TourProvider = ({ children }) => {
  const [currentTour, setCurrentTour] = useState(null);
  const [tourStep, setTourStep] = useState(0);
  const [isTourOpen, setIsTourOpen] = useState(false);

  // Start a tour (normal behavior - respects session closure and preferences)
  const startTour = useCallback((tourData) => {
    // Check if tour should be prevented from showing
    if (shouldPreventTourDisplay(tourData)) {
      console.log(`🎯 Tour "${tourData.name}" prevented from showing`);
      return false;
    }

    setCurrentTour(tourData);
    setTourStep(0);
    setIsTourOpen(true);
    console.log(`🎯 Starting tour: ${tourData.name}`);
    return true;
  }, []);

  // Force show a tour with options for bypassing checks
  const forceShowTour = useCallback((tourData) => {
    // Check for absolute bypass flag
    if (tourData.bypassAllChecks === true) {
      console.log(`🎯 ABSOLUTELY forcing tour "${tourData.name}" - bypassing ALL checks`);
      setCurrentTour(tourData);
      setTourStep(0);
      setIsTourOpen(true);
      return true;
    }

    // Only check permanent "don't show again" preference for normal force show
    if (hasPermanentDisablePreference(tourData.name)) {
      console.log(`🎯 Tour "${tourData.name}" permanently disabled, cannot force show`);
      return false;
    }

    const enhancedTourData = {
      ...tourData,
      forceShow: true // This flag tells the overlay to ignore session closure
    };
    
    setCurrentTour(enhancedTourData);
    setTourStep(0);
    setIsTourOpen(true);
    console.log(`🎯 Force showing tour: ${tourData.name}`);
    return true;
  }, []);

  // Helper function to check if tour should be prevented
  const shouldPreventTourDisplay = useCallback((tourData) => {
    if (!tourData || !tourData.name) return false;
    
    // If tour was explicitly requested (force show), don't prevent it
    if (tourData.forceShow === true) {
      return false;
    }
    
    // Check permanent "don't show again" preference
    if (hasPermanentDisablePreference(tourData.name)) {
      return true;
    }
    
    // Check if tour was closed in this session
    if (isTourClosedInSession(tourData.name)) {
      return true;
    }
    
    return false;
  }, []);

  // Check permanent disable preference
  const hasPermanentDisablePreference = useCallback((tourName) => {
    try {
      const tourPreferences = localStorage.getItem('tourPreferences');
      if (tourPreferences) {
        const preferences = JSON.parse(tourPreferences);
        const tourPref = preferences[tourName];
        return tourPref && tourPref.dontShowAgain;
      }
    } catch (error) {
      console.error('Error checking tour preferences:', error);
    }
    return false;
  }, []);

  // Check if tour was closed in this session
  const isTourClosedInSession = useCallback((tourName) => {
    try {
      const closedTours = sessionStorage.getItem('closedTours');
      if (closedTours) {
        const closedToursArray = JSON.parse(closedTours);
        return closedToursArray.includes(tourName);
      }
    } catch (error) {
      console.error('Error checking session storage:', error);
    }
    return false;
  }, []);

  const nextStep = useCallback(() => {
    if (currentTour && tourStep < currentTour.steps.length - 1) {
      setTourStep(prev => prev + 1);
    }
  }, [currentTour, tourStep]);

  const prevStep = useCallback(() => {
    if (tourStep > 0) {
      setTourStep(prev => prev - 1);
    }
  }, [tourStep]);

  const endTour = useCallback((dontShowAgain = false) => {
    // Save "don't show again" preference if requested
    if (dontShowAgain && currentTour && currentTour.name) {
      try {
        const tourPreferences = localStorage.getItem('tourPreferences');
        const preferences = tourPreferences ? JSON.parse(tourPreferences) : {};
        preferences[currentTour.name] = {
          dontShowAgain: true,
          timestamp: Date.now()
        };
        localStorage.setItem('tourPreferences', JSON.stringify(preferences));
        console.log(`🎯 Tour "${currentTour.name}" marked as "don't show again"`);
      } catch (error) {
        console.error('Error saving tour preference:', error);
      }
    }

    setIsTourOpen(false);
    setCurrentTour(null);
    setTourStep(0);
    console.log(`🎯 Tour ended. Don't show again: ${dontShowAgain}`);
  }, [currentTour]);

  const skipTour = useCallback(() => {
    endTour(false);
  }, [endTour]);

  // Clear session closure for a specific tour (useful for manual resets)
  const clearSessionClosure = useCallback((tourName) => {
    try {
      const closedTours = sessionStorage.getItem('closedTours');
      if (closedTours) {
        const closedToursArray = JSON.parse(closedTours);
        const updatedArray = closedToursArray.filter(name => name !== tourName);
        sessionStorage.setItem('closedTours', JSON.stringify(updatedArray));
        console.log(`🎯 Session closure cleared for tour: ${tourName}`);
      }
    } catch (error) {
      console.error('Error clearing session closure:', error);
    }
  }, []);

  // Clear permanent preference for a specific tour
  const clearPermanentPreference = useCallback((tourName) => {
    try {
      const tourPreferences = localStorage.getItem('tourPreferences');
      if (tourPreferences) {
        const preferences = JSON.parse(tourPreferences);
        if (preferences[tourName]) {
          delete preferences[tourName];
          localStorage.setItem('tourPreferences', JSON.stringify(preferences));
          console.log(`🎯 Permanent preference cleared for tour: ${tourName}`);
        }
      }
    } catch (error) {
      console.error('Error clearing permanent preference:', error);
    }
  }, []);

  const value = {
    currentTour,
    tourStep,
    isTourOpen,
    startTour,
    forceShowTour, // Now supports bypassAllChecks flag
    nextStep,
    prevStep,
    endTour,
    skipTour,
    clearSessionClosure,
    clearPermanentPreference,
    // Helper methods for checking tour state
    shouldPreventTourDisplay,
    hasPermanentDisablePreference,
    isTourClosedInSession,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};